import config from '../../config';

import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { extractErrorMessageFromAPI, redirectToLogin } from '../../utils';

const baseQuery = fetchBaseQuery({
  baseUrl: `${import.meta.env.VITE_ENV_HOST_URL || config.urls.host}`,
  credentials: 'include',
});

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  // List of public routes where redirect should not occur
  const publicRoutes = [
    '/reset-password',
    '/forgot-password',
    '/mail-approval',
    '/onboarding',
    '/device-limit',
    '/signin',
  ];

  if (result.error && result.error.status === 401) {
    const path = window.location.pathname;
    if (!publicRoutes.includes(path)) {
      const message = extractErrorMessageFromAPI(result.error);
      const errorCode = (result.error.data as any)?.errorCode;

      // SESSION_TERMINATED means this session was force-logged-out from another device.
      // Redirect immediately without the sessionStorage guard — the user must re-login.
      if (errorCode === 'SESSION_TERMINATED') {
        // Broadcast so other tabs (e.g. device-limit page) know a slot opened
        try {
          const bc = new BroadcastChannel('session_events');
          bc.postMessage({ type: 'SESSION_LOGOUT' });
          bc.close();
        } catch { /* BroadcastChannel not supported — ignore */ }
        window.location.replace('/signin');
      } else if (
        message === 'User is not authenticated' &&
        !sessionStorage.getItem('redirected')
      ) {
        // Standard unauthenticated redirect (check-session 401, etc.)
        sessionStorage.setItem('redirected', 'true');
        setTimeout(() => redirectToLogin(), 100);
      }
    }
  }

  return result;
};
